/**
 * `#intelCaveats` — design §7: "always visible", built from `summary.limitations` (the
 * server's own dataset-freshness/season-fallback lines, already localised) plus the six
 * fixed caveat lines the plan's Global Constraints name (`intel.caveats.*`): dataset-year
 * mismatch, resolution mix, survey coverage, crop-mask uncertainty, "no data ≠ absence",
 * SWI not used.
 *
 * GAEZ round: UC3 gains two more lines, the same "always visible" register the UC2
 * small-pool line already uses — the 9 km modelled-nature sentence (every crop, every run)
 * and, only for a crop with no BACS mask (sorghum, millet), the crop-area rule's own
 * `gaez_fallback` sentence. `cropAreaRule` is `uc3/config`'s own served text when the caller
 * has it; the locale string is the fallback a fresh install (or a stale cache) falls back
 * to, never a silently missing caveat.
 */
import { useTranslation } from 'react-i18next';

import type { IntelSummary } from '../../lib/api';

const FIXED_CAVEAT_KEYS = [
  'datasetYear',
  'resolution',
  'surveyCoverage',
  'cropMask',
  'noData',
  'swi',
] as const;

const NO_MASK_CROPS = new Set(['sorghum', 'millet']);

export function Caveats({
  summary,
  useCase,
  crop,
  cropAreaRule,
}: {
  summary?: IntelSummary | null;
  useCase?: string;
  /** UC3 only — the reader's chosen crop id, to decide whether the crop-area rule applies. */
  crop?: string;
  /** UC3 only — `uc3/config`'s own `crop_area_rule.gaez_fallback`, when the caller has it. */
  cropAreaRule?: { en: string; fr: string } | null;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'fr' ? 'fr' : 'en';
  const limitations =
    (summary?.summary as { limitations?: string[] } | undefined)?.limitations ?? [];

  return (
    <div id="intelCaveats" className="intel-caveats">
      <h5>{t('intel.caveats.title')}</h5>
      <ul>
        {FIXED_CAVEAT_KEYS.map((key) => (
          <li key={key}>{t(`intel.caveats.${key}`)}</li>
        ))}
        {/* Amendment 1 §3.2: UC2's 11-watershed similarity pool is small enough to name. */}
        {useCase === 'uc3' ? (
          <>
            <li>{t('intel.caveats.uc3GaezModelled')}</li>
            {crop && NO_MASK_CROPS.has(crop) ? (
              <li>{cropAreaRule ? cropAreaRule[lang] : t('intel.caveats.uc3CropAreaRule')}</li>
            ) : null}
          </>
        ) : null}
        {limitations.map((line, i) => (
          // Server-built, already localised (`?lang=`) — printed as it arrived.
          <li key={i}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

export default Caveats;
